import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaUserCircle, FaCreditCard, FaUsers, FaSignOutAlt, FaPhone, FaSitemap, FaCalendarAlt, FaCalendarCheck, FaCalendarPlus, FaCalendarTimes, FaUserFriends } from 'react-icons/fa';
import { FEATURE_FLAGS } from '../constants';
import Seats from '../pages/Seats';
import PhoneSlots from '../pages/PhoneSlots';
import CalendarConnectModal from './CalendarConnectModal';
import CalendarDisconnectModal from './CalendarDisconnectModal';
import apiClient from '../services/apiClient';

const NavBar = ({ onSignInClick, onProfileClick, onBillingClick, onSeatsUpdate, onPhoneSlotsUpdate }) => {
  const { user, logout, isOwner, isAdmin, canManageUsersTeams, canManageBilling, isImpersonating, impersonatedUser, endImpersonation } = useAuth();
  const navigate = useNavigate();
  const [showHomeSubmenu, setShowHomeSubmenu] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showSeatsModal, setShowSeatsModal] = useState(false);
  const [showPhoneSlotsModal, setShowPhoneSlotsModal] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Calendar auth state (members only)
  const [calendarStatus, setCalendarStatus] = useState(null); // null | 'loading' | 'connected' | 'unconnected' | 'error'
  const [calendarProvider, setCalendarProvider] = useState(null);
  const [showCalendarConnectModal, setShowCalendarConnectModal] = useState(false);
  const [showCalendarDisconnectModal, setShowCalendarDisconnectModal] = useState(false);
  const [showCalBtnTooltip, setShowCalBtnTooltip] = useState(false);

  const checkCalendarStatus = useCallback(async () => {
    setCalendarStatus('loading');
    // During impersonation use the impersonated user's email so the backend
    // looks up their calendar token instead of the Account Owner's.
    const targetEmail = isImpersonating && impersonatedUser?.email
      ? impersonatedUser.email
      : null;
    console.log(`[NavBar] checkCalendarStatus — email: ${targetEmail || '(jwt-user)'}`);
    try {
      const url = targetEmail
        ? `/api/calendar/auth/status?target_email=${encodeURIComponent(targetEmail)}`
        : '/api/calendar/auth/status';
      const res = await apiClient.get(url);
      if (res.data.calendar_sync_enabled === false) {
        // Member's personal_calendar_sync is disabled — hide the button entirely
        setCalendarStatus(null);
        setCalendarProvider(null);
        return;
      }
      if (res.data.connected) {
        setCalendarStatus('connected');
        setCalendarProvider(res.data.provider);
      } else {
        setCalendarStatus('unconnected');
        setCalendarProvider(null);
      }
    } catch (e) {
      setCalendarStatus('error');
    }
  }, [isImpersonating, impersonatedUser]);

  // Auto-check on login / impersonation change for all users
  useEffect(() => {
    if (user) {
      checkCalendarStatus();
    } else {
      setCalendarStatus(null);
      setCalendarProvider(null);
    }
  }, [user, checkCalendarStatus]);
  
  // Handle trial badge click
  const handleTrialClick = () => {
    navigate('/signup?plan=trial');
  };
  
  // Add CSS animation for the Connect Calendar button
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes gentlePulse {
        0%, 100% { 
          box-shadow: 0 4px 14px rgba(102, 126, 234, 0.35);
        }
        50% { 
          box-shadow: 0 4px 14px rgba(102, 126, 234, 0.55), 0 0 0 4px rgba(102, 126, 234, 0.1);
        }
      }
      
      @keyframes iconSpin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      @keyframes shimmer {
        0% { transform: translateX(-100%); opacity: 0; }
        50% { opacity: 1; }
        100% { transform: translateX(100%); opacity: 0; }
      }
      
      .connect-calendar-button {
        animation: gentlePulse 3s ease-in-out infinite;
        position: relative;
        overflow: hidden;
      }
      
      .connect-calendar-button:hover {
        animation: none !important;
      }
      
      .connect-calendar-button::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
        animation: shimmer 4s ease-in-out infinite;
        animation-delay: 2s;
      }
      
      .calendar-icon {
        display: inline-block;
        transition: transform 0.3s ease;
      }
      
      .connect-calendar-button:hover .calendar-icon {
        animation: iconSpin 0.6s ease-in-out;
      }
      
      @keyframes trialPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.03); }
      }
      
      @keyframes flagFly {
        0%, 100% { transform: translateY(0px) translateX(0px) rotate(0deg); }
        25% { transform: translateY(-3px) translateX(2px) rotate(-2deg); }
        50% { transform: translateY(-5px) translateX(0px) rotate(0deg); }
        75% { transform: translateY(-3px) translateX(-2px) rotate(2deg); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Handle seats update callback
  const handleSeatsUpdate = (newSeatCount, updatedBillingData) => {
    if (onSeatsUpdate) {
      onSeatsUpdate(newSeatCount, updatedBillingData);
    }
  };

  // Handle phone slots update callback
  const handlePhoneSlotsUpdate = () => {
    setShowPhoneSlotsModal(false);
    if (onPhoneSlotsUpdate) {
      onPhoneSlotsUpdate();
    }
  };
  
  useEffect(() => {
    // Close dropdowns when clicking outside
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowHomeSubmenu(false);
        setShowUserDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  const navStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 2px 0px',
    borderBottom: '1px solid #ccc',
    marginBottom: '0',
    background: '#fff',
    position: 'relative', // Add position to help with z-index
    zIndex: 100, // Add z-index to ensure visibility
    boxShadow: 'none',
  };

  const dropdownStyle = {
    position: 'absolute',
    top: 'calc(100% + 5px)',
    right: 0, // align to right edge of parent (for user dropdown)
    left: 'auto',
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: '8px',
    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
    padding: '8px 0',
    zIndex: 101,
    minWidth: '160px',
    maxWidth: 'calc(100vw - 24px)',
    boxSizing: 'border-box',
    overflow: 'hidden',
    transition: 'opacity 0.2s ease',
  };

  const leftDropdownStyle = {
    position: 'absolute',
    top: 'calc(100% + 5px)',
    left: 0, // align to left edge of parent (for Home/Privacy dropdown)
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: '8px',
    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
    padding: '8px 0',
    zIndex: 101,
    minWidth: '150px',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
  };

  const visibleDropdownStyle = {
    ...dropdownStyle,
    transform: 'translateY(0)',
    opacity: 1,
  };

  const visibleLeftDropdownStyle = {
    ...leftDropdownStyle,
    transform: 'translateY(0)',
    opacity: 1,
  };
  
  const dropdownItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 18px',
    color: '#222',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    borderLeft: '3px solid transparent',
    fontSize: '1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
  };

  // Button reset so dropdown <button> elements look identical to the old <a> items
  const dropdownBtnStyle = {
    ...dropdownItemStyle,
    background: 'transparent',
    border: 'none',
    borderLeft: '3px solid transparent',
    width: '100%',
    textAlign: 'left',
    outline: 'none',
  };

  const activeDropdownItemStyle = {
    ...dropdownItemStyle,
    backgroundColor: 'rgba(13, 71, 161, 0.13)',
    color: '#0d47a1',
    borderLeft: '3px solid #0d47a1',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(13, 71, 161, 0.08)',
  };
  
  const dropdownItemHoverStyle = {
    backgroundColor: 'rgba(13, 71, 161, 0.10)',
    borderLeft: '3px solid #0d47a1',
    color: '#0d47a1',
    boxShadow: 'inset 0 0 0 1px rgba(13, 71, 161, 0.12)',
    transform: 'none',
  };

  const activeDropdownItemHoverStyle = {
    backgroundColor: 'rgba(13, 71, 161, 0.18)',
    borderLeft: '3px solid #0a3f91',
    color: '#0d47a1',
    boxShadow: 'inset 0 0 0 1px rgba(13, 71, 161, 0.15)',
    transform: 'none',
  };

  const signInStyle = {
    textDecoration: 'none',
    color: '#0d47a1',
    fontWeight: 500,
    padding: '8px 16px',
    borderRadius: '4px',
    transition: 'all 0.3s ease',
    transform: 'translateY(0)',
    display: 'inline-block',
  };
  
  const signInHoverStyle = {
    backgroundColor: 'rgba(13, 71, 161, 0.08)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(13, 71, 161, 0.15)',
  };
  
  const userMenuStyle = {
    display: 'flex',
    alignItems: 'center',
  };
  
  const avatarStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    color: '#757575',
    fontWeight: 'bold',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    transform: 'translateY(0)',
  };
  
  const avatarHoverStyle = {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
  };

  return (
    <nav style={navStyle} className="main-navbar debug-navbar">
      <div ref={dropdownRef} style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>

        {/* Impersonation banner — centered, only visible during impersonation */}
        {isImpersonating && impersonatedUser && (
          <div style={{
            position: 'absolute', left: '50%', transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#fef3c7', border: '1px solid #f59e0b',
            borderRadius: 20, padding: '4px 6px 4px 12px',
            fontSize: 13, color: '#92400e', fontWeight: 600,
            zIndex: 10, whiteSpace: 'nowrap', pointerEvents: 'auto',
            boxShadow: '0 2px 8px rgba(245,158,11,0.2)',
          }}>
            <span style={{ fontSize: 12 }}>👤</span>
            <span>Viewing as {[impersonatedUser.name, impersonatedUser.last_name].filter(Boolean).join(' ') || impersonatedUser.email}</span>
            <button
              onClick={() => { endImpersonation(); navigate('/users'); }}
              style={{
                background: '#92400e', color: '#fff', border: 'none',
                borderRadius: 12, padding: '3px 10px',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#78350f'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#92400e'; }}
            >
              Exit Impersonation
            </button>
          </div>
        )}

        {/* Tab bar */}
        <div style={{ display: 'flex', alignItems: 'center', background: '#edf0f5', borderRadius: '6px', padding: '3px', gap: '2px' }}>

          {/* Home tab (with dropdown) */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setShowHomeSubmenu(!showHomeSubmenu); setShowUserDropdown(false); }}
              style={{
                background: (location.pathname === '/' || location.pathname === '/privacy' || location.pathname === '/team-feature') ? 'white' : 'transparent',
                border: 'none',
                borderRadius: '4px',
                padding: '7px 18px',
                fontWeight: (location.pathname === '/' || location.pathname === '/privacy' || location.pathname === '/team-feature') ? 700 : 500,
                color: (location.pathname === '/' || location.pathname === '/privacy' || location.pathname === '/team-feature') ? '#0d47a1' : '#555',
                cursor: 'pointer',
                fontSize: '14px',
                boxShadow: (location.pathname === '/' || location.pathname === '/privacy' || location.pathname === '/team-feature') ? '0 1px 5px rgba(0,0,0,0.13)' : 'none',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: '5px'
              }}
            >
              Home <span style={{ fontSize: '10px', opacity: 0.7 }}>▾</span>
            </button>
            {showHomeSubmenu && (
              <div style={visibleLeftDropdownStyle}>
                <Link to="/" style={location.pathname === '/' ? activeDropdownItemStyle : dropdownItemStyle}
                  onMouseEnter={e => Object.assign(e.currentTarget.style, location.pathname === '/' ? activeDropdownItemHoverStyle : dropdownItemHoverStyle)}
                  onMouseLeave={e => { for (const k in dropdownItemHoverStyle) e.currentTarget.style[k] = (location.pathname === '/' ? activeDropdownItemStyle : dropdownItemStyle)[k] || ''; }}
                  onClick={() => setShowHomeSubmenu(false)}
                >Home</Link>
                <Link to="/privacy" style={location.pathname === '/privacy' ? activeDropdownItemStyle : dropdownItemStyle}
                  onMouseEnter={e => Object.assign(e.currentTarget.style, location.pathname === '/privacy' ? activeDropdownItemHoverStyle : dropdownItemHoverStyle)}
                  onMouseLeave={e => { for (const k in dropdownItemHoverStyle) e.currentTarget.style[k] = (location.pathname === '/privacy' ? activeDropdownItemStyle : dropdownItemStyle)[k] || ''; }}
                  onClick={() => setShowHomeSubmenu(false)}
                >Privacy</Link>
                <Link to="/team-feature" style={location.pathname === '/team-feature' ? activeDropdownItemStyle : dropdownItemStyle}
                  onMouseEnter={e => Object.assign(e.currentTarget.style, location.pathname === '/team-feature' ? activeDropdownItemHoverStyle : dropdownItemHoverStyle)}
                  onMouseLeave={e => { for (const k in dropdownItemHoverStyle) e.currentTarget.style[k] = (location.pathname === '/team-feature' ? activeDropdownItemStyle : dropdownItemStyle)[k] || ''; }}
                  onClick={() => setShowHomeSubmenu(false)}
                >Team Feature</Link>
              </div>
            )}
          </div>

          {/* Connect Calendar / Teams tab — owner, admin with manage_users, or Lead Agent */}
          {user && (isOwner || canManageUsersTeams || user.can_manage_teams) && (
            <Link
              to="/register"
              style={{
                background: location.pathname === '/register' ? 'white' : 'transparent',
                borderRadius: '4px',
                padding: '7px 18px',
                fontWeight: location.pathname === '/register' ? 700 : 500,
                color: location.pathname === '/register' ? '#0d47a1' : '#555',
                textDecoration: 'none',
                fontSize: '14px',
                boxShadow: location.pathname === '/register' ? '0 1px 5px rgba(0,0,0,0.13)' : 'none',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: '6px'
              }}
              onMouseEnter={e => { if (location.pathname !== '/register') { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = '#0d47a1'; } }}
              onMouseLeave={e => { if (location.pathname !== '/register') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#555'; } }}
              onClick={() => window.dispatchEvent(new Event("register-tab-clicked"))}
            >
              <FaSitemap style={{ fontSize: '13px' }} />
              Teams
            </Link>
          )}

          {/* Users tab — Account Owners and admins with manage_users */}
          {user && (isOwner || canManageUsersTeams) && (
            <Link
              to="/users"
              style={{
                background: location.pathname === '/users' ? 'white' : 'transparent',
                borderRadius: '4px',
                padding: '7px 18px',
                fontWeight: location.pathname === '/users' ? 700 : 500,
                color: location.pathname === '/users' ? '#0d47a1' : '#555',
                textDecoration: 'none',
                fontSize: '14px',
                boxShadow: location.pathname === '/users' ? '0 1px 5px rgba(0,0,0,0.13)' : 'none',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: '6px'
              }}
              onMouseEnter={e => { if (location.pathname !== '/users') { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = '#0d47a1'; } }}
              onMouseLeave={e => { if (location.pathname !== '/users') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#555'; } }}
            >
              <FaUserFriends style={{ fontSize: '13px' }} />
              Users
            </Link>
          )}

          {/* Tasks tab */}
          {user && (
            <Link
              to="/tasks"
              style={{
                background: location.pathname === '/tasks' ? 'white' : 'transparent',
                borderRadius: '4px',
                padding: '7px 18px',
                fontWeight: location.pathname === '/tasks' ? 700 : 500,
                color: location.pathname === '/tasks' ? '#0d47a1' : '#555',
                textDecoration: 'none',
                fontSize: '14px',
                boxShadow: location.pathname === '/tasks' ? '0 1px 5px rgba(0,0,0,0.13)' : 'none',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: '6px'
              }}
              onMouseEnter={e => { if (location.pathname !== '/tasks') { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = '#0d47a1'; } }}
              onMouseLeave={e => { if (location.pathname !== '/tasks') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#555'; } }}
            >
              <span style={{ fontSize: '13px' }}>📅</span>
              Tasks
            </Link>
          )}

          {/* Calendar tab */}
          {user && (
            <Link
              to="/calendar"
              style={{
                background: location.pathname === '/calendar' ? 'white' : 'transparent',
                borderRadius: '4px',
                padding: '7px 18px',
                fontWeight: location.pathname === '/calendar' ? 700 : 500,
                color: location.pathname === '/calendar' ? '#0d47a1' : '#555',
                textDecoration: 'none',
                fontSize: '14px',
                boxShadow: location.pathname === '/calendar' ? '0 1px 5px rgba(0,0,0,0.13)' : 'none',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: '6px'
              }}
              onMouseEnter={e => { if (location.pathname !== '/calendar') { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = '#0d47a1'; } }}
              onMouseLeave={e => { if (location.pathname !== '/calendar') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#555'; } }}
            >
              <FaCalendarAlt style={{ fontSize: '13px' }} />
              Calendar
            </Link>
          )}

          {/* Unpublished Changes tab — Account Owners and admins with manage_users */}
          {user && (isOwner || canManageUsersTeams) && (
            <Link
              to="/pending-changes"
              style={{
                background: location.pathname === '/pending-changes' ? 'white' : 'transparent',
                borderRadius: '4px',
                padding: '7px 18px',
                fontWeight: location.pathname === '/pending-changes' ? 700 : 500,
                color: location.pathname === '/pending-changes' ? '#0d47a1' : '#555',
                textDecoration: 'none',
                fontSize: '14px',
                boxShadow: location.pathname === '/pending-changes' ? '0 1px 5px rgba(0,0,0,0.13)' : 'none',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: '6px'
              }}
              onMouseEnter={e => { if (location.pathname !== '/pending-changes') { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = '#0d47a1'; } }}
              onMouseLeave={e => { if (location.pathname !== '/pending-changes') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#555'; } }}
            >
              <span style={{ fontSize: '13px' }}>⏳</span>
              Unshared
            </Link>
          )}

        </div>

        <div>
          {user ? (
            <div style={userMenuStyle}>
              {/* Your Calendar button — right-aligned, left of profile icon */}
              {calendarStatus !== null && (() => {
                const isConnected = calendarStatus === 'connected';
                const isLoading = calendarStatus === 'loading';
                const isError = calendarStatus === 'error';
                const btnBg = isConnected ? '#10b981' : isLoading ? '#9ca3af' : isError ? '#ef4444' : '#f59e0b';
                const btnHover = isConnected ? '#059669' : isLoading ? '#9ca3af' : isError ? '#dc2626' : '#d97706';
                const CalIcon = isConnected ? FaCalendarCheck
                  : isLoading ? FaCalendarAlt
                  : isError ? FaCalendarTimes
                  : FaCalendarPlus;
                const providerLabel = calendarProvider === 'google' ? 'Google'
                  : calendarProvider === 'outlook' ? 'Outlook'
                  : calendarProvider === 'icloud' ? 'iCloud'
                  : '';
                const btnLabel = isConnected
                  ? `${providerLabel} Calendar Connected`
                  : isLoading
                  ? 'Checking Calendar…'
                  : isError
                  ? 'Calendar Check Failed'
                  : 'Connect Calendar';
                const tooltipText = isImpersonating
                  ? `Viewing ${providerLabel ? providerLabel + ' ' : ''}calendar status for impersonated user. Calendar management is disabled during impersonation.`
                  : isConnected
                  ? `${providerLabel} Calendar is connected. Click to manage or disconnect.`
                  : isLoading
                  ? 'Checking your calendar connection…'
                  : isError
                  ? 'Could not reach calendar service. Click to retry.'
                  : 'Connect your calendar to sync real estate tasks and deadlines automatically.';
                return (
                  <div
                    style={{ position: 'relative', marginRight: '8px' }}
                    onMouseEnter={() => setShowCalBtnTooltip(true)}
                    onMouseLeave={() => setShowCalBtnTooltip(false)}
                  >
                    <button
                      disabled={isLoading || isImpersonating}
                      className={!isConnected && !isLoading && !isError && !isImpersonating ? 'connect-calendar-button' : ''}
                      onClick={() => {
                        if (isImpersonating) return;
                        if (isConnected) setShowCalendarDisconnectModal(true);
                        else if (!isLoading) setShowCalendarConnectModal(true);
                      }}
                      style={{
                        background: btnBg, color: '#fff', border: 'none',
                        borderRadius: '20px', padding: '4px 12px',
                        cursor: isLoading || isImpersonating ? 'default' : 'pointer',
                        fontSize: '12px', fontWeight: 600,
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap',
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        boxShadow: `0 2px 8px ${btnBg}66`,
                        opacity: isImpersonating ? 0.85 : 1,
                      }}
                      onMouseEnter={(e) => { if (!isLoading && !isImpersonating) e.currentTarget.style.background = btnHover; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = btnBg; }}
                    >
                      <CalIcon className="calendar-icon" style={{ fontSize: '13px' }} />
                      {btnLabel}
                    </button>
                    {/* Tooltip */}
                    {showCalBtnTooltip && (
                      <div style={{
                        position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                        backgroundColor: '#fff', border: '1px solid #eee',
                        borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                        padding: '8px 12px', whiteSpace: 'nowrap',
                        fontSize: '12px', color: '#334155', fontWeight: 500,
                        pointerEvents: 'none', zIndex: 200,
                      }}>
                        {/* Arrow */}
                        <div style={{
                          position: 'absolute', top: '-5px', right: '16px',
                          transform: 'rotate(45deg)',
                          width: '8px', height: '8px',
                          backgroundColor: '#fff', border: '1px solid #eee',
                          borderBottom: 'none', borderRight: 'none',
                        }} />
                        {tooltipText}
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* Upgrade Plan Badge for Trial Users and Invited Members */}
              {(user.accountType === 'trial' || user.accountType === 'Trial') && (
                <div 
                  style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 100%)',
                    border: '1px solid #f59e0b',
                    borderRadius: '12px',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    color: '#92400e',
                    fontWeight: '500',
                    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginRight: '8px'
                  }}
                  onClick={() => {
                    if (onBillingClick) {
                      onBillingClick();
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.25)';
                    e.target.style.background = 'linear-gradient(135deg, #fef3c7 0%, #f59e0b 100%)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0px)';
                    e.target.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.15)';
                    e.target.style.background = 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 100%)';
                  }}
                >
                  <FaCreditCard style={{ fontSize: '12px', color: '#92400e' }} />
                  <span style={{
                    color: '#92400e',
                    fontSize: '11px',
                    fontWeight: '500',
                    letterSpacing: '0.2px'
                  }}>
                    Upgrade Plan
                  </span>
                </div>
              )}
              
              <div 
                style={avatarStyle}
                tabIndex={0}
                onClick={() => { setShowUserDropdown(v => !v); setShowHomeSubmenu(false); }}
                onKeyDown={e => { if (e.key === 'Enter') setShowUserDropdown(v => !v); }}
                onMouseEnter={e => { Object.assign(e.target.style, avatarHoverStyle); }}
                onMouseLeave={e => { for (const property in avatarHoverStyle) { e.target.style[property] = avatarStyle[property] || ''; } }}
              >
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
              <span 
                style={{ marginRight: '15px', cursor: 'pointer', fontWeight: 500, borderRadius: '4px', transition: 'background 0.2s' }}
                tabIndex={0}
                onClick={() => setShowUserDropdown(v => !v)}
                onKeyDown={e => { if (e.key === 'Enter') setShowUserDropdown(v => !v); }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                onMouseLeave={e => { e.currentTarget.style.background = ''; }}
              >{user.name}</span>
              
              <div style={{ position: 'relative' }}>
                <span
                  style={{ cursor: 'pointer', fontSize: 18, marginLeft: 2 }}
                  onClick={() => setShowUserDropdown(v => !v)}
                  tabIndex={0}
                  aria-label="User menu"
                >▼</span>
                {showUserDropdown && (
                  <div style={visibleDropdownStyle}>
                    <button
                      type="button"
                      style={dropdownBtnStyle}
                      onClick={() => { if (typeof onProfileClick === 'function') onProfileClick(); }}
                      onMouseEnter={e => Object.assign(e.currentTarget.style, dropdownItemHoverStyle)}
                      onMouseLeave={e => {
                        for (const key in dropdownItemHoverStyle) {
                          e.currentTarget.style[key] = dropdownBtnStyle[key] || '';
                        }
                      }}
                    >
                      <FaUserCircle style={{ fontSize: '1.2em', color: '#0d47a1' }} />
                      Profile
                    </button>
                    {(isOwner || canManageBilling) && (
                      <button
                        type="button"
                        style={dropdownBtnStyle}
                        onClick={() => { if (typeof onBillingClick === 'function') onBillingClick(); }}
                        onMouseEnter={e => Object.assign(e.currentTarget.style, dropdownItemHoverStyle)}
                        onMouseLeave={e => {
                          for (const key in dropdownItemHoverStyle) {
                            e.currentTarget.style[key] = dropdownBtnStyle[key] || '';
                          }
                        }}
                      >
                        <FaCreditCard style={{ fontSize: '1.1em', color: '#0d47a1' }} />
                        Billing
                      </button>
                    )}
                    {(isOwner || canManageBilling) && (
                      <button
                        type="button"
                        style={dropdownBtnStyle}
                        onClick={() => { setShowSeatsModal(true); setShowUserDropdown(false); }}
                        onMouseEnter={e => Object.assign(e.currentTarget.style, dropdownItemHoverStyle)}
                        onMouseLeave={e => {
                          for (const key in dropdownItemHoverStyle) {
                            e.currentTarget.style[key] = dropdownBtnStyle[key] || '';
                          }
                        }}
                      >
                        <FaUsers style={{ fontSize: '1.1em', color: '#0d47a1' }} />
                        Seats
                      </button>
                    )}
                    {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && (isOwner || canManageBilling) && (
                      <button
                        type="button"
                        style={dropdownBtnStyle}
                        onClick={() => { setShowPhoneSlotsModal(true); setShowUserDropdown(false); }}
                        onMouseEnter={e => Object.assign(e.currentTarget.style, dropdownItemHoverStyle)}
                        onMouseLeave={e => {
                          for (const key in dropdownItemHoverStyle) {
                            e.currentTarget.style[key] = dropdownBtnStyle[key] || '';
                          }
                        }}
                      >
                        <FaPhone style={{ fontSize: '1.1em', color: '#28a745' }} />
                        Slots
                      </button>
                    )}
                    <div style={{ borderTop: '1px solid #eee', margin: '8px 0' }}></div>
                    <button
                      type="button"
                      style={dropdownBtnStyle}
                      onClick={() => logout()}
                      onMouseEnter={e => Object.assign(e.currentTarget.style, dropdownItemHoverStyle)}
                      onMouseLeave={e => {
                        for (const key in dropdownItemHoverStyle) {
                          e.currentTarget.style[key] = dropdownBtnStyle[key] || '';
                        }
                      }}
                    >
                      <FaSignOutAlt style={{ fontSize: '1.1em', color: '#b71c1c' }} />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Enhanced Free Trial Badge */}
              <div 
                style={{
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                  border: '1px solid #22c55e',
                  borderRadius: '12px',
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  color: '#166534',
                  fontWeight: '500',
                  boxShadow: '0 2px 8px rgba(34, 197, 94, 0.15)',
                  position: 'relative',
                  overflow: 'visible',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={handleTrialClick}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.25)';
                  e.target.style.background = 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0px)';
                  e.target.style.boxShadow = '0 2px 8px rgba(34, 197, 94, 0.15)';
                  e.target.style.background = 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)';
                }}
              >
                <div style={{ 
                  fontSize: '18px',
                  opacity: 1,
                  position: 'relative',
                  width: '20px',
                  height: '14px',
                  animation: 'flagFly 2s ease-in-out infinite'
                }}>
                  {/* Flag pole */}
                  <div style={{
                    position: 'absolute',
                    left: '2px',
                    top: '0px',
                    width: '2px',
                    height: '14px',
                    backgroundColor: '#8b5a3c'
                  }}></div>
                  {/* Green flag */}
                  <div style={{
                    position: 'absolute',
                    left: '4px',
                    top: '2px',
                    width: '14px',
                    height: '8px',
                    backgroundColor: '#16a34a',
                    clipPath: 'polygon(0 0, 80% 0, 100% 50%, 80% 100%, 0 100%)'
                  }}></div>
                </div>
                <span style={{
                  color: '#166534',
                  fontSize: '13px',
                  fontWeight: '500',
                  letterSpacing: '0.2px'
                }}>
                  Free 14-Day Trial
                </span>
              </div>
              
              {/* Enhanced Sign In Button */}
              <button
                type="button"
                style={{
                  ...signInStyle,
                  background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)',
                  color: 'white',
                  fontWeight: '600',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(13, 71, 161, 0.3)',
                  border: 'none',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  Object.assign(e.target.style, {
                    ...signInHoverStyle,
                    background: 'linear-gradient(135deg, #0a3f91 0%, #1357b0 100%)',
                    boxShadow: '0 4px 12px rgba(13, 71, 161, 0.4)',
                    transform: 'translateY(-2px)'
                  });
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)';
                  e.target.style.boxShadow = '0 2px 8px rgba(13, 71, 161, 0.3)';
                  e.target.style.transform = 'translateY(0)';
                }}
                onClick={() => { if (onSignInClick) { onSignInClick(); } }}
              >
                Sign In
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Seats Modal */}
      <Seats 
        show={showSeatsModal} 
        onClose={() => setShowSeatsModal(false)} 
        onSeatsUpdate={handleSeatsUpdate}
      />

      {/* Phone Slots Modal */}
      <PhoneSlots
        show={showPhoneSlotsModal}
        onClose={() => setShowPhoneSlotsModal(false)}
        onSlotsUpdate={handlePhoneSlotsUpdate}
      />

      {/* Calendar Connect Modal */}
      {showCalendarConnectModal && (
        <CalendarConnectModal
          onClose={() => setShowCalendarConnectModal(false)}
          onSuccess={() => {
            setShowCalendarConnectModal(false);
            checkCalendarStatus();
          }}
        />
      )}

      {/* Calendar Disconnect Modal */}
      {showCalendarDisconnectModal && (
        <CalendarDisconnectModal
          provider={calendarProvider}
          onClose={() => setShowCalendarDisconnectModal(false)}
          onSuccess={() => {
            setShowCalendarDisconnectModal(false);
            checkCalendarStatus();
          }}
        />
      )}
    </nav>
  );
};

export default NavBar;
